import { Route, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ROUTES } from "./constants";
import { PermissionGuard } from "../guard/PermissionGuard";
import GlobalLayout from "../shared/components/templates/GlobalLayout/GlobalLayout";
import { PERMISSIONS } from "@/shared/stores";
import FeatureBoundaryProvider from "./FeatureBoundaryProvider";
import LoadingSpinner from "../shared/components/ui/LoadingSpinner/LoadingSpinner";

const Home = lazy(() => import("../pages/Home"));
const MedicalRecordPage = lazy(() => import("../pages/MedicalRecord"));
const NewMedicalRecordPage = lazy(() => import("../pages/NewMedicalRecord"));
const ProfessionalsPage = lazy(() => import("../pages/Professionals"));
const MPIPage = lazy(() => import("../pages/MPI"));
const PatientDetailPage = lazy(() => import("../pages/MPI/PatientDetail"));
const DataAnalysisPage = lazy(() => import("@/pages/DataAnalysis/DataPage"));
const AirportsDataPage = lazy(
  () => import("@/pages/DataAnalysis/AirportsDataPage"),
);
const PatientTimelinePage = lazy(() => import("@/pages/PatientTimeline"));
const AuditRegistersPage = lazy(() => import("@/pages/AuditRegisters"));
const AuditMedicalRecord = lazy(() => import("@/pages/AuditMedicalRecordPage"));
const CreatePatientPage = lazy(() => import("@/pages/MPI/CreatePatient"));
const NotFoundPage = lazy(() => import("../pages/NotFound"));

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<GlobalLayout />}>
      <Route
        element={
          <Suspense fallback={<LoadingSpinner isLoading={true} />}>
            <FeatureBoundaryProvider />
          </Suspense>
        }
      >
        {/* Ruta Home protegida */}
        <Route
          element={
            <PermissionGuard requiredPermissions={PERMISSIONS.canViewHome} />
          }
        >
          <Route path={ROUTES.HOME} element={<Home />} />
        </Route>

        <Route
          element={
            <PermissionGuard
              requiredPermissions={PERMISSIONS.canViewClinicalHistory}
            />
          }
        >
          <Route
            path={ROUTES.AUDIT_MEDICAL_RECORD}
            element={<AuditMedicalRecord />}
          />
        </Route>
        <Route
          element={
            <PermissionGuard
              requiredPermissions={PERMISSIONS.canViewClinicalHistory}
            />
          }
        >
          <Route
            path={ROUTES.AUDIT_REGISTERS}
            element={<AuditRegistersPage />}
          />
        </Route>

        {/* Ruta Historias Clínicas protegida */}
        <Route
          element={
            <PermissionGuard
              requiredPermissions={[
                PERMISSIONS.canEnterClinicalHistory,
                PERMISSIONS.canLoadClinicalHistory,
              ]}
              mode="any"
            />
          }
        >
          <Route path={ROUTES.MEDICAL_RECORD} element={<MedicalRecordPage />} />
        </Route>

        <Route
          element={
            <PermissionGuard
              requiredPermissions={PERMISSIONS.canLoadClinicalHistory}
            />
          }
        >
          <Route
            path={ROUTES.NEW_MEDICAL_RECORD}
            element={<NewMedicalRecordPage />}
          />
        </Route>

        <Route
          element={
            <PermissionGuard
              requiredPermissions={PERMISSIONS.canViewClinicalHistory}
            />
          }
        >
          <Route
            path={ROUTES.PATIENT_TIMELINE}
            element={<PatientTimelinePage />}
          />
        </Route>

        {/* Ruta Profesionales protegida */}
        <Route
          element={
            <PermissionGuard
              requiredPermissions={PERMISSIONS.canViewProfessionals}
            />
          }
        >
          <Route path={ROUTES.PROFESSIONALS} element={<ProfessionalsPage />} />
        </Route>

        {/* Rutas MPI protegidas */}
        <Route
          element={
            <PermissionGuard requiredPermissions={PERMISSIONS.canViewMPI} />
          }
        >
          <Route path={ROUTES.MPI} element={<MPIPage />} />
          <Route path={ROUTES.MPI_PATIENT_DETAIL} element={<PatientDetailPage />} />
        </Route>

        <Route
          element={
            <PermissionGuard requiredPermissions={PERMISSIONS.canEditMPI} />
          }
        >
          <Route path={ROUTES.MPI_CREATE_PATIENT} element={<CreatePatientPage />} />
        </Route>

        {/* Ruta Análisis de Datos protegida */}
        <Route
          element={
            <PermissionGuard
              requiredPermissions={PERMISSIONS.canViewDataAnalysis}
            />
          }
        >
          <Route path={ROUTES.DATA_ANALYSIS} element={<DataAnalysisPage />} />
          <Route
            path={ROUTES.DATA_ANALYSIS_AIRPORTS}
            element={<AirportsDataPage />}
          />
        </Route>
      </Route>
      <Route
        path="*"
        element={
          <Suspense
            fallback={
              <LoadingSpinner
                isLoading={true}
                message="Cargando página..."
                delay={0}
              />
            }
          >
            <NotFoundPage />
          </Suspense>
        }
      />
    </Route>
  )
);
